import {
  List,
  ListItem,
  Tag,
  Text,
  useColorModeValue,
  useMediaQuery,
} from "@chakra-ui/react";
import { Currency, DEFAULT_CURRENCY, formatPrice } from "@models/currency";
import { NFTEvent, NFTEventTier } from "@models/event";
import { DEFAULT_LANGUAGE, translate } from "@models/language";
import { Timezone } from "@models/timezone";
import { formatDate } from "@models/utils";
import { isEmpty } from "lodash";
import { useMemo } from "react";

const t = (key: string) => key;

const StyledTag = (props) => (
  <Tag
    fontWeight={500}
    fontSize="sm"
    borderRadius={4}
    bgColor="primary.300"
    textColor="#FFFFFF"
    mr={2}
    textTransform="capitalize"
    {...props}
  >
    {props.children}
  </Tag>
);

const TicketItem = ({
  tier,
  currency,
  timezone,
}: {
  tier: NFTEventTier;
  currency: Currency;
  timezone: Timezone;
}) => {
  const [isMobile] = useMediaQuery("(max-width: 640px)");
  const language = DEFAULT_LANGUAGE;

  const discount = useMemo(
    () => tier?.paymentConfig?.currentPriceDiscountPercentage || 0,
    [tier]
  );
  const price = useMemo(
    () => tier?.paymentConfig?.currentPrice.price || 0,
    [tier]
  );

  const priceName = useMemo(
    () =>
      translate(tier?.paymentConfig?.currentPrice.info, language).name || "",
    [tier]
  );

  const priceEnd = useMemo(
    () =>
      tier?.paymentConfig?.currentPrice.end
        ? `Until ${formatDate(
            tier.paymentConfig.currentPrice.end,
            timezone,
            "MMM dd"
          )}`
        : "",
    [tier, timezone]
  );

  const defaultPrice = useMemo(
    () => tier?.paymentConfig?.defaultPrice.price || 0,
    [tier]
  );

  const tierName = useMemo(
    () => translate(tier.info, language).name || tier.name,
    [language]
  );
  const textColor = useColorModeValue("#444444", "#DEDEE0");

  const whitelistOnly = useMemo(
    () => !isEmpty(tier?.paymentConfig?.whitelist),
    [tier]
  );

  console.log({ whitelistOnly });

  if (!tier.paymentConfig) return <></>;
  return (
    <ListItem w="full">
      <div className="flex flex-row w-full items-center">
        <div className="flex flex-row items-center flex-grow justify-start">
          <p className="sm:flex sm:flex-row items-center mr-2 font-base text-lg">
            <span
              className="mr-2 text-base font-medium"
              style={{ color: textColor }}
            >
              {tierName}
            </span>
            {tier.paymentConfig.inviteOnly && (
              <StyledTag>{t("Invite only")}</StyledTag>
            )}
            {discount > 0 && (
              <StyledTag>
                {`${discount}% Off ${!isMobile ? priceEnd : ""}`}
              </StyledTag>
            )}
            {tier.paymentConfig.nftConstraint && (
              <StyledTag bgColor="#F5F3FB" textColor="#7B61FF">
                {t("Holder only")}
              </StyledTag>
            )}
            {whitelistOnly && (
              <StyledTag bgColor="#F5F3FB" textColor="#7B61FF">
                {t("Whitelist Only")}
              </StyledTag>
            )}
          </p>
        </div>

        {/* Price */}
        <div className="flex flex-shrink-0">
          <Text fontWeight={500} fontSize="lg" color="neutral.300">
            {price === 0 ? (
              <span className="uppercase" style={{ color: textColor }}>
                {t("free")}
              </span>
            ) : (
              <span>
                <span
                  className={`text-base mx-1 text-base ${
                    discount > 0 && "line-through"
                  }`}
                  style={{ color: textColor }}
                >
                  {formatPrice(defaultPrice, currency)}
                </span>
                {discount > 0 && (
                  <span
                    className="text-base mx-1 no-underline"
                    style={{ color: textColor }}
                  >
                    {formatPrice(price, currency)}
                  </span>
                )}
              </span>
            )}
          </Text>
        </div>
      </div>
    </ListItem>
  );
};

const EventDetailTicketType = ({ event }: { event: NFTEvent }) => {
  const tiers = useMemo(() => {
    if (!event) return [];
    return event.tiers.filter((tier) => tier.paymentConfig);
  }, [event]);

  const timezone = useMemo(() => {
    return event?.timezone || Timezone["Asia/Hong_Kong"];
  }, [event]);

  const currency = useMemo(() => {
    return event?.currency || DEFAULT_CURRENCY;
  }, [event]);

  const textColor = useColorModeValue("", "white");

  if (isEmpty(tiers)) {
    return <></>;
  }

  return (
    <div className="mb-16 px-0 mb-16 w-full">
      <div className="flex justify-between items-center">
        <Text fontSize="2xl" fontWeight="bold" color={textColor}>
          Asset Type
        </Text>
      </div>
      <List mt="4" spacing={6}>
        {tiers.map((tier, i) => (
          <TicketItem
            key={i}
            tier={tier}
            currency={currency}
            timezone={timezone}
          />
        ))}
      </List>
    </div>
  );
};

export default EventDetailTicketType;
